// UI copy for the feedback survey, keyed by language.
//
// IMPORTANT: option `value`s stay in English on purpose — they are the data
// sent to the Google Apps Script backend, so answers from the EN and RU pages
// land in the same spreadsheet columns. Only the visible `label`/`title`/`desc`
// text is translated. Never translate a `value`.

const en = {
  meta: {
    htmlLang: "en",
    title: "Distortion Grids Database — Survey | Matchmove machine",
    description:
      "A short survey about the Distortion Grids Database and future subscription options. Complete it and get a free preset.",
  },
  header: {
    h1: "Distortion Grids Database",
    subtitle:
      "A short survey, 3-5 minutes. Your feedback directly shapes our library and subscription frameworks.",
    giftBadge: "🎁 Complete it and get a free preset from the library",
  },
  sections: {
    about: "About you",
    pipeline: "Pipeline & Tooling",
    cost: "The Real Cost of Grid Shooting",
    subscription: "Subscription Architecture",
    final: "Final Validation",
  },
  email: {
    label: "Account Email (used for platform login & preset code)",
    placeholder: "you@domain.com",
  },
  role: {
    label: "Your role",
    selectPlaceholder: "Select your role...",
    otherPlaceholder: "Please specify your role",
    options: [
      { value: "Matchmove artist", label: "Matchmove artist" },
      { value: "Compositing artist", label: "Compositing artist" },
      { value: "CG artist", label: "CG artist" },
      {
        value: "Data wrangler / On-set supervisor",
        label: "Data wrangler / On-set supervisor",
      },
      { value: "VFX supervisor", label: "VFX supervisor" },
      { value: "Layout artist", label: "Layout artist" },
      {
        value: "Studio Representative / Manager",
        label: "Studio Representative / Manager",
      },
      { value: "Other", label: "Other" },
    ],
  },
  studio: {
    label: "Studio / company name",
    placeholder: "E.g., Studio, Company, Freelancer, etc.",
  },
  q1: {
    label:
      "1. Which software/pipeline do you mainly use tracking tools and grids in? (select all that apply)",
    otherPlaceholder: "Please specify software",
    options: [
      { value: "3DEqualizer", label: "3DEqualizer" },
      { value: "Nuke", label: "Nuke" },
      { value: "SynthEyes", label: "SynthEyes" },
      { value: "PFTrack", label: "PFTrack" },
      { value: "Other", label: "Other" },
    ],
  },
  q2: {
    label:
      "2. What is your biggest bottleneck or pain point when looking for exact sensor dimensions or matching lens distortion parameters?",
    placeholder:
      "E.g., finding the exact focal length, dealing with cropped sensors, matching overscan, etc.",
  },
  q3: {
    before: "3. How often do you use our Camera Database (",
    link: "camdb.matchmovemachine.com",
    after:
      ") to cross-check sensor sizes and resolution metadata?",
    options: [
      { value: "On every single project", label: "On every single project" },
      { value: "Frequently", label: "Frequently" },
      { value: "Rarely / Not yet", label: "Rarely / Not yet" },
      { value: "Never heard of it", label: "Never heard of it" },
    ],
  },
  q4: {
    label:
      "4. Have you ever had to manually alter or approximate a grid from the Distortion Grids Database because of a camera resolution mismatch?",
    detailPlaceholder: "What technical parameters did you alter?",
    options: [
      { value: "Yes, often", label: "Yes, often" },
      { value: "Sometimes", label: "Sometimes" },
      { value: "No, never", label: "No, never" },
    ],
  },
  cost: {
    h3: "Have you ever calculated the true cost of shooting grids?",
    leadBefore: "We built a ",
    leadLink: "Grid Cost Calculator",
    leadAfter: " to help studios estimate the real expenses involved.",
    callout: {
      before1: "Shooting a custom 4-lens set can cost anywhere from ",
      price1: "$830",
      before2: " to over ",
      price2: "$3,000",
      before3:
        " and take more than a full working day in gear rentals, studio time, and labor. In contrast, our ready-made presets cost around ",
      price3: "$125 or less",
      after:
        " (depending on your subscription tier), allowing artists to bypass tedious preparation and start tracking instantly at a fraction of the cost.",
    },
    q5Label:
      "5. On average, how many lenses do you typically shoot grids for per project?",
    q5Options: [
      { value: "1-3 lenses", label: "1-3 lenses" },
      { value: "4-6 lenses", label: "4-6 lenses" },
      { value: "7-10 lenses", label: "7-10 lenses" },
      { value: "More than 10 lenses", label: "More than 10 lenses" },
    ],
  },
  q6: {
    label:
      "6. Do you feel the time and budget spent on shooting custom grids is justified, or would you prefer a ready-made database?",
    placeholder:
      "E.g., We spend too much time renting gear vs. It's necessary for our specific workflow...",
  },
  q7: {
    label:
      "7. Which user profile tier best maps onto your current operational scale?",
    options: [
      {
        value: "Hobby",
        title: "Hobby",
        desc: "Personal exploration / Non-commercial",
      },
      {
        value: "Freelancer",
        title: "Freelancer",
        desc: "Independent commercial production",
      },
      {
        value: "Studio",
        title: "Studio",
        desc: "Scalable pipeline team architecture",
      },
    ],
  },
  q8: {
    label:
      "8. Which upcoming subscription framework structure fits your project access needs best?",
    options: [
      {
        value: "On-demand access",
        title: "On-demand framework",
        desc: "Pay/subscribe only per active show or project scale",
      },
      {
        value: "Continuous access",
        title: "Continuous access framework",
        desc: "Steady rolling plan for ongoing multi-project needs",
      },
      {
        value: "Enterprise pipeline",
        title: "Enterprise pipeline framework",
        desc: "Uncapped studio-wide access with custom API integration",
      },
    ],
  },
  q9: {
    label:
      "9. What features or pricing model would convince you to subscribe instead of buying grids one by one?",
    placeholder:
      'E.g., "Unlimited access to all grids", "Direct API integration for our pipeline", or a specific monthly rate.',
  },
  q10: {
    label:
      "10. Assuming subscription levels are clear and fit your needs, how likely are you to choose a recurring package over individual on-demand asset purchases?",
  },
  q11: {
    label:
      "11. How likely are you to recommend the Distortion Grids Database to an industry colleague?",
  },
  q12: {
    label:
      "12. Open feedback: Is there anything else you want to see built into the Camera Database or Distortion Grids Database workflows?",
  },
  scaleHint: "1 — completely unlikely, 5 — highly likely",
  consent:
    "I agree to be contacted by Matchmove machine via email regarding my preset reward and this survey.",
  submit: "Submit answers",
  submitting: "Submitting…",
  clear: "Clear all fields",
  clearConfirm: "Tap again to confirm",
  validationError: "Please fill in all required fields to continue.",
  success:
    "Thank you! Your answers were submitted.<br>We'll email your preset code within a few days.",
  submitFailed: "Submission failed. Please try again.",
};

const ru = {
  meta: {
    htmlLang: "ru",
    title: "Distortion Grids Database — Опрос | Matchmove machine",
    description:
      "Короткий опрос о Distortion Grids Database и будущих вариантах подписки. Пройдите его и получите бесплатный пресет.",
  },
  header: {
    h1: "Distortion Grids Database",
    subtitle:
      "Короткий опрос на 3-5 минут. Ваши ответы напрямую повлияют на развитие библиотеки и форматы подписки.",
    giftBadge: "🎁 Пройдите опрос и получите бесплатный пресет из библиотеки",
  },
  sections: {
    about: "О вас",
    pipeline: "Пайплайн и инструменты",
    cost: "Реальная стоимость съемки гридов",
    subscription: "Архитектура подписки",
    final: "Финальные вопросы",
  },
  email: {
    label:
      "Email аккаунта (используется для входа на платформу и получения пресета)",
    placeholder: "ваш@email.com",
  },
  role: {
    label: "Ваша специализация",
    selectPlaceholder: "Выберите специализацию...",
    otherPlaceholder: "Пожалуйста, укажите вашу специализацию",
    options: [
      { value: "Matchmove artist", label: "Matchmove artist" },
      { value: "Compositing artist", label: "Compositing artist" },
      { value: "CG artist", label: "CG artist" },
      {
        value: "Data wrangler / On-set supervisor",
        label: "Data wrangler / On-set supervisor",
      },
      { value: "VFX supervisor", label: "VFX supervisor" },
      { value: "Layout artist", label: "Layout artist" },
      {
        value: "Studio Representative / Manager",
        label: "Представитель студии / Менеджер",
      },
      { value: "Other", label: "Другое" },
    ],
  },
  studio: {
    label: "Название студии / компании",
    placeholder: "Например: Название студии или Фрилансер",
  },
  q1: {
    label:
      "1. В каком софте/пайплайне вы в основном используете инструменты трекинга и гриды? (выберите все подходящие варианты)",
    otherPlaceholder: "Укажите софт",
    options: [
      { value: "3DEqualizer", label: "3DEqualizer" },
      { value: "Nuke", label: "Nuke" },
      { value: "SynthEyes", label: "SynthEyes" },
      { value: "PFTrack", label: "PFTrack" },
      { value: "Other", label: "Другое" },
    ],
  },
  q2: {
    label:
      "2. В чем ваша главная проблема или «узкое место» при поиске точных размеров сенсора или параметров дисторсии объектива?",
    placeholder:
      "Например: поиск точного фокусного расстояния, работа с кропнутыми сенсорами, расчет оверскана и т.д.",
  },
  q3: {
    before: "3. Как часто вы используете нашу базу камер (",
    link: "camdb.matchmovemachine.com",
    after:
      ") для проверки размеров сенсоров и метаданных разрешения?",
    options: [
      { value: "On every single project", label: "На каждом проекте" },
      { value: "Frequently", label: "Часто" },
      { value: "Rarely / Not yet", label: "Редко / Пока нет" },
      { value: "Never heard of it", label: "Никогда не слышал(а) о ней" },
    ],
  },
  q4: {
    label:
      "4. Приходилось ли вам вручную изменять или подгонять грид из Distortion Grids Database из-за несовпадения разрешения камеры?",
    detailPlaceholder: "Какие технические параметры вы изменяли?",
    options: [
      { value: "Yes, often", label: "Да, часто" },
      { value: "Sometimes", label: "Иногда" },
      { value: "No, never", label: "Нет, никогда" },
    ],
  },
  cost: {
    h3: "Вы когда-нибудь считали реальную стоимость съемки гридов?",
    leadBefore: "Мы создали ",
    leadLink: "Калькулятор стоимости гридов",
    leadAfter: ", чтобы помочь студиям оценить реальные расходы.",
    callout: {
      before1: "Съемка кастомного набора из 4 объективов может стоить от ",
      price1: "$830",
      before2: " до более чем ",
      price2: "$3,000",
      before3:
        " и занять целый рабочий день (аренда оборудования, студия, работа команды). Напротив, наши готовые пресеты стоят около ",
      price3: "$125 или меньше",
      after:
        " (в зависимости от уровня подписки), позволяя артистам пропустить нудную подготовку и начать трекинг мгновенно за малую часть этой стоимости.",
    },
    q5Label:
      "5. В среднем, для скольких объективов вы снимаете гриды на одном проекте?",
    q5Options: [
      { value: "1-3 lenses", label: "1-3 объектива" },
      { value: "4-6 lenses", label: "4-6 объективов" },
      { value: "7-10 lenses", label: "7-10 объективов" },
      { value: "More than 10 lenses", label: "Более 10 объективов" },
    ],
  },
  q6: {
    label:
      "6. Считаете ли вы, что время и бюджет, затрачиваемые на съемку своих гридов, оправданы, или вы предпочли бы готовую базу данных?",
    placeholder:
      "Например: Мы тратим слишком много времени на аренду оборудования vs. Это необходимо для нашего специфического воркфлоу...",
  },
  q7: {
    label:
      "7. Какой профиль пользователя лучше всего описывает масштаб вашей работы?",
    options: [
      {
        value: "Hobby",
        title: "Хобби",
        desc: "Личные проекты / Некоммерческое использование",
      },
      {
        value: "Freelancer",
        title: "Фрилансер",
        desc: "Независимое коммерческое производство",
      },
      {
        value: "Studio",
        title: "Студия",
        desc: "Масштабируемый командный пайплайн",
      },
    ],
  },
  q8: {
    label:
      "8. Какой из будущих форматов подписки лучше всего подходит для ваших проектов?",
    options: [
      {
        value: "On-demand access",
        title: "Формат «По запросу»",
        desc: "Оплата/подписка только на период активного проекта",
      },
      {
        value: "Continuous access",
        title: "Формат непрерывного доступа",
        desc: "Постоянный план для регулярной работы над множеством проектов",
      },
      {
        value: "Enterprise pipeline",
        title: "Enterprise пайплайн",
        desc: "Неограниченный доступ для всей студии с интеграцией API",
      },
    ],
  },
  q9: {
    label:
      "9. Какие функции или модель ценообразования убедили бы вас оформить подписку вместо покупки гридов поштучно?",
    placeholder:
      "Например: «Безлимитный доступ ко всем гридам», «Прямая интеграция API в наш пайплайн» или определенная ежемесячная стоимость.",
  },
  q10: {
    label:
      "10. Предполагая, что условия подписки вам подходят, насколько вероятно, что вы выберете регулярную подписку вместо разовых покупок?",
  },
  q11: {
    label:
      "11. Насколько вероятно, что вы порекомендуете Distortion Grids Database коллеге по индустрии?",
  },
  q12: {
    label:
      "12. Открытый отзыв: Есть ли что-то еще, что вы хотели бы видеть в рабочих процессах Camera Database или Distortion Grids Database?",
  },
  scaleHint: "1 — совершенно невероятно, 5 — очень вероятно",
  consent:
    "Я согласен(на), чтобы Matchmove machine связались со мной по email по поводу получения пресета и данного опроса.",
  submit: "Отправить ответы",
  submitting: "Отправка…",
  clear: "Очистить все поля",
  clearConfirm: "Нажмите еще раз для подтверждения",
  validationError:
    "Пожалуйста, заполните все обязательные поля для продолжения.",
  success:
    "Спасибо! Ваши ответы отправлены.<br>Мы вышлем код на пресет на ваш email в течение нескольких дней.",
  submitFailed: "Ошибка отправки. Пожалуйста, попробуйте еще раз.",
};

export const SURVEY_COPY = { en, ru };

export default SURVEY_COPY;
